/**
 * One insight result becomes the words and numbers a card draws.
 *
 * Pure, and tested without a device, for the same reason `entry-format.ts` is:
 * every rule here reads a binding or a field definition, never a business
 * word. The card components take the output of this file and lay it out. They
 * format nothing themselves, which is the `StatTile` contract.
 *
 * Three jobs.
 *
 *   the label    what the card is called. A template-named card keeps its
 *                key; a card the user assembled gets a sentence built from
 *                its measure, its window and its group.
 *   the number   money in the record's currency, a plain count, or a name
 *                when the insight ranked groups instead of summing them.
 *   the tone     which way a signed number points. The direction is carried
 *                by the sign, a word and an arrow. Colour only ever repeats
 *                what those already say, because our green and red separate
 *                by 4.4 Delta E under deuteranopia.
 *
 * **And one more, at the end of the file: how much of that a SIZE shows.** How
 * far down a ranking a Wide breakdown reads, how many periods a Wide bars card
 * keeps, which rung a Small figure takes — those are decisions with an answer
 * that can be wrong, and they live here rather than in the six components for
 * the reason everything else here does: a component imports `react-native` and
 * `react-native` does not load under `bun test`, so a rule written inside one
 * is a rule nothing can hold. The components keep the pixels; this keeps the
 * counts.
 */
import type {
  InsightAggLeaf,
  InsightAggNode,
  RecordSchemaField,
  RecordSchemaResponse,
} from '@alluzo/shared';

import { formatDayShort, formatMoney, formatNumber, formatWeekday } from '@/lib/entry-format';
import { t, tData, tOr } from '@/lib/i18n';
import type { EntryFieldFilter, EntryFilterSet, EntryWindow } from '@/lib/entry-filter';
import type { InsightCardId } from '@/lib/insight-cards';
import type { InsightLayoutEntry } from '@/lib/insight-layout';
import { measureFieldOf } from '@/lib/insight-layout';
import { formulaCurrencyOf } from '@/lib/worked-out';
import { PRESENCE_OPS, NUMERIC_OPS } from '@/lib/insight-query';
import type {
  BreakdownResult,
  InsightFilter,
  InsightMeasure,
  InsightResult,
  InsightScope,
  InsightSpec,
  PairResult,
  ScalarResult,
  SeriesResult,
} from '@/lib/insight-query';

/** Which way a number points, once the sign has been read. */
export type InsightTone = 'neutral' | 'positive' | 'negative';

export type PresentedSlice = {
  /** The group's name, or its raw key when nothing could name it. */
  readonly label: string;
  readonly value: string;
  /** 0 to 1, against the largest slice. What a proportion bar draws. */
  readonly fraction: number;
};

export type Presented = {
  readonly id: string;
  readonly card: InsightCardId;
  readonly span: InsightLayoutEntry['span'];
  /**
   * The rectangle it takes on the RECORD's grid, beside the span the Dashboard
   * strip still lays out by — never instead of it (`REDESIGN.md` §12).
   *
   * A card does not read this on its own. `InsightCardProps.drawAs` is what
   * tells a card to draw at a size, and only `InsightGrid` passes it; the
   * strip, the gallery and the editor's preview pass nothing and get the
   * drawing they have always had. This field is here because the GRID reads it
   * — `pack()` needs one rectangle per insight — and because a card handed
   * `drawAs` is being handed this value.
   */
  readonly size: InsightLayoutEntry['size'];
  readonly label: string;
  /** The headline. Empty when there is nothing to show yet. */
  readonly value: string;
  readonly tone: InsightTone;
  /** True when the query answered nothing at all, so the card draws quietly. */
  readonly empty: boolean;
  /** A pair card's second line: "18% vs last month", or the no-compare word. */
  readonly change: { readonly percent: string; readonly against: string; readonly tone: InsightTone } | null;
  /** A pair card's bar: how far `value` reaches against `against`, 0 to 1. */
  readonly progress: number | null;
  /** A series card's numbers, oldest first. */
  readonly points: readonly { readonly day: string; readonly value: number }[];
  /** A series card's latest number, already formatted. */
  readonly latest: string | null;
  /** A breakdown card's rows, largest first. */
  readonly slices: readonly PresentedSlice[];
  /** A breakdown card's netted tail. */
  readonly other: { readonly label: string; readonly value: string } | null;
  /**
   * A line under the headline, when the headline needs one: grouped by a
   * multi select, "An entry can be in more than one group", so nobody adds
   * up the groups and gets a wrong total (Decision 8).
   */
  readonly note?: string;
  /**
   * True when the number is WORKED OUT rather than read from a column: a
   * recipe run on every row, or totals combined with arithmetic.
   *
   * The card draws a small mark beside its label for it. A stored number and a
   * worked-out one are two different promises, and a card that read the same
   * for both would hide which one it is making.
   */
  readonly worked: boolean;
  /**
   * True when this number is a SAMPLE, not the business's own.
   *
   * Set only by `insight-sample.ts`, and only while no record in the business
   * has an entry. The card draws the number at half strength for it and leaves
   * its label at full contrast, because what is not real is exactly what is
   * faded. The strip says so once in words above the cards.
   */
  readonly sample: boolean;
};

/* ----------------------------------------------------------------- numbers */

const fieldById = (
  schema: RecordSchemaResponse,
  fieldId: string,
): RecordSchemaField | undefined => schema.fields.find((field) => field.id === fieldId);

/**
 * The schema of every record this one's references point at, by record id.
 *
 * A card may group by, or ask about, a field on the row a reference points at —
 * "by the customer's area". The far field lives on the other record, so
 * naming it needs the other record's schema. `use-hop-targets.ts` is what a
 * screen holding one already loads; a caller that has none gets the honest
 * fallback below.
 */
export type HopSchemas = ReadonlyMap<string, RecordSchemaResponse>;

const NO_TARGETS: HopSchemas = new Map();

/**
 * The field a hop reaches, or null when this device cannot name it.
 *
 * Null has two causes and one answer: the reference is gone, or the record it
 * points at has not been pulled. Either way the caller falls back to the id,
 * which is the rule `formulaSentence` keeps — an id is ugly and honest, and a
 * blank hides the field that caused it.
 */
const hopFieldOf = (
  schema: RecordSchemaResponse,
  via: string,
  fieldId: string,
  targets: HopSchemas,
): { readonly via: RecordSchemaField; readonly field: RecordSchemaField } | null => {
  const through = fieldById(schema, via);
  if (through === undefined || through.targetRecordId === null) return null;
  const target = targets.get(through.targetRecordId);
  const field = target === undefined ? undefined : fieldById(target, fieldId);
  return field === undefined ? null : { via: through, field };
};

/** "Customer's Area", or the plainest true thing when the far side is unknown. */
const hopWord = (
  schema: RecordSchemaResponse,
  via: string,
  fieldId: string,
  targets: HopSchemas,
): string => {
  const held = hopFieldOf(schema, via, fieldId, targets);
  const viaLabel = held?.via.label ?? fieldById(schema, via)?.label ?? via;
  return t('insight.hop_field', { via: viaLabel, field: held?.field.label ?? fieldId });
};

/** What a card groups by, whether that is its own field or one a hop away. */
const groupWord = (
  schema: RecordSchemaResponse,
  fieldId: string,
  via: string | undefined,
  targets: HopSchemas,
): string =>
  via === undefined
    ? (fieldById(schema, fieldId)?.label ?? '')
    : hopWord(schema, via, fieldId, targets);

/**
 * The currency a total is drawn in, or null when the number is not money.
 *
 * A combined card is never drawn as money. Its leaves may span two records and
 * two units, and there is no honest way to name a currency for the arithmetic
 * over them — a plain number says less and claims nothing false. A recipe
 * measure is the same: the tree may end in a rate, a count or a total.
 *
 * A money field is in the record's currency. A worked-out field that answers
 * in money (the visit's Bill) is in the record's currency or else the
 * business's, by the form's own rule (`formulaCurrencyOf`): a record that
 * counts visits carries no currency, and its Bill is still rupees.
 */
const moneyCurrencyOf = (
  schema: RecordSchemaResponse,
  measure: InsightMeasure,
  businessCurrency: string | null,
): string | null => {
  if (measure.agg === 'rows' || measure.agg === 'count' || measure.agg === 'formula') return null;
  if (measure.of.kind === 'expr') return null;
  const fieldId = measure.of.kind === 'measure' ? measureFieldOf(schema) : measure.of.fieldId;
  const field = fieldId === null ? undefined : fieldById(schema, fieldId);
  if (field === undefined) return null;
  if (field.type === 'money') return schema.currency;
  return formulaCurrencyOf(field, schema.currency, businessCurrency);
};

/**
 * A number, in the units the insight is actually in.
 *
 * A count is never money, however the record is set up: counting sales does
 * not produce rupees. `formatMoney` falls back to a plain number when there is
 * no currency, so a currency-less record is safe here.
 */
const asAmount = (
  schema: RecordSchemaResponse,
  measure: InsightMeasure,
  value: number | null,
  businessCurrency: string | null,
): string => {
  if (value === null) return '';
  const currency = moneyCurrencyOf(schema, measure, businessCurrency);
  return currency === null ? formatNumber(value) : formatMoney(value, currency);
};

/* ------------------------------------------------------------------ labels */

/**
 * The word for what a group key is. A reference resolves; a select is a key.
 *
 * Exported because a filter chip names the same ids a card's condition does,
 * and two spellings of one name is one too many.
 */
export const nameForKey = (
  schema: RecordSchemaResponse,
  groupFieldId: string,
  key: string,
  names: Readonly<Record<string, string>>,
): string => {
  const resolved = names[key];
  if (resolved !== undefined && resolved !== '') return resolved;
  const field = fieldById(schema, groupFieldId);
  const option = field?.options?.find((candidate) => candidate.id === key);
  if (option !== undefined) return option.label ?? tData(option.labelKey);
  return key;
};

/**
 * The word for one group key, whichever record's field the key belongs to.
 *
 * A card grouped through a reference is keyed by a field on the OTHER record —
 * "by the customer's area" — so the choices that name those keys live on the
 * other record's schema. Without it the bars would be labelled with option
 * ids, which is a card drawn in a language nobody reads.
 */
const keyWord = (
  schema: RecordSchemaResponse,
  fieldId: string,
  via: string | undefined,
  key: string,
  names: Readonly<Record<string, string>>,
  targets: HopSchemas,
): string => {
  if (via === undefined) return nameForKey(schema, fieldId, key, names);
  const held = hopFieldOf(schema, via, fieldId, targets);
  const there = held === null ? null : targets.get(held.via.targetRecordId ?? '');
  return nameForKey(there ?? schema, fieldId, key, names);
};

/**
 * The record's own word for one row. `tOr`: the key is the record's data, and
 * a composed record may carry a noun the catalogue does not hold.
 */
const entryNoun = (schema: RecordSchemaResponse): string => tOr(schema.entryNounKey, t('noun.entry', { count: 1 }), { count: 1 });

/**
 * What the measure is called, for a card nothing named.
 *
 * A recipe has no one field to be named after, and a combined card has no one
 * record, so both fall back to the record's own noun. The sentence they end up
 * in is a placeholder either way: a card built from a recipe is named by the
 * person who built it, and that name wins over everything here.
 */
export const isWorkedOut = (measure: InsightMeasure): boolean => {
  if (measure.agg === 'formula') return true;
  if (measure.agg === 'rows') return false;
  return measure.of.kind === 'expr';
};

/** Whether any total of a tree counts a record other than this one. */
const readsTwoRecords = (schema: RecordSchemaResponse, node: InsightAggNode): boolean => {
  if (node.kind === 'agg') {
    return node.recordId !== undefined && node.recordId !== schema.recordId;
  }
  if (node.kind === 'calc') return readsTwoRecords(schema, node.left) || readsTwoRecords(schema, node.right);
  if (node.kind === 'round') return readsTwoRecords(schema, node.of);
  return false;
};

/** What one total adds up, in the record's own word for it. */
const leafWord = (schema: RecordSchemaResponse, leaf: InsightAggLeaf): string => {
  if (leaf.agg === 'rows' || leaf.of === undefined) return entryNoun(schema);
  if (leaf.of.kind === 'expr') return t('insight.combine.a_recipe');
  const fieldId = leaf.of.kind === 'measure' ? measureFieldOf(schema) : leaf.of.fieldId;
  const field = fieldId === null ? undefined : fieldById(schema, fieldId);
  return field?.label ?? entryNoun(schema);
};

/**
 * What a combined card is called when nobody named it, joined by the operators
 * between its totals.
 *
 * WHICH WORD depends on what the card reaches. Across two records it is their
 * names — "Deliveries minus Payments" — because the record is the one word
 * that is true of a total whatever it adds up, and a card reaching two of them
 * says both or says nothing. Within ONE record they are all the same word,
 * so it is the fields instead: "Amount divided by Quantity" is the average
 * rate, and "Deliveries divided by Deliveries" is not a name at all.
 *
 * A record this device has no word for falls back to its id rather than to a
 * blank, which is the rule `formulaSentence` keeps: an id is ugly and honest,
 * and a blank hides the record that caused it.
 *
 * @param names record id to the tenant's own name. The Dashboard's presenter
 *              hands its catalogue in; a record's own screen has only itself,
 *              and every total of a card living there is that record anyway.
 */
const combinedWord = (
  schema: RecordSchemaResponse,
  expr: InsightAggNode,
  names: Readonly<Record<string, string>>,
): string => {
  const twoRecords = readsTwoRecords(schema, expr);
  const say = (node: InsightAggNode): string => {
    if (node.kind === 'agg') {
      if (!twoRecords) return leafWord(schema, node);
      const recordId = node.recordId;
      if (recordId === undefined || recordId === schema.recordId) return schema.name;
      return names[recordId] ?? recordId;
    }
    if (node.kind === 'const') return String(node.value);
    if (node.kind === 'round') return say(node.of);
    return `${say(node.left)} ${t(`formula.op.${node.op}_word`)} ${say(node.right)}`;
  };
  return say(expr);
};

const measureWord = (schema: RecordSchemaResponse, measure: InsightMeasure): string => {
  if (measure.agg === 'rows' || measure.agg === 'formula') return entryNoun(schema);
  if (measure.of.kind === 'measure') {
    const measureFieldId = measureFieldOf(schema);
    const field = measureFieldId === null ? undefined : fieldById(schema, measureFieldId);
    return field?.label ?? entryNoun(schema);
  }
  if (measure.of.kind === 'expr') return entryNoun(schema);
  return fieldById(schema, measure.of.fieldId)?.label ?? entryNoun(schema);
};

const scopeWord = (scope: InsightScope): string => t(`insight.scope.${scope}`);

/* -------------------------------------------------------------- conditions */

/** How many conditions a derived name spells out before it counts the rest. */
const CONDITIONS_IN_A_NAME = 2;

/**
 * One condition, in words.
 *
 * The field's own label carries the noun, so nothing here knows what a payment
 * mode is. A comparison shows its number in the field's units, which is why a
 * money field's condition reads in rupees and a quantity's does not.
 *
 * Exported because the editor draws the same sentence on its condition chips.
 * One spelling, two screens.
 */
export const describeFilter = (
  schema: RecordSchemaResponse,
  filter: InsightFilter,
  names: Readonly<Record<string, string>> = {},
  targets: HopSchemas = NO_TARGETS,
): string => {
  // A condition through a reference asks about the OTHER record's field, so
  // both the noun and the value's own word come from there.
  const hop = filter.via === undefined ? null : hopFieldOf(schema, filter.via, filter.fieldId, targets);
  const field = filter.via === undefined ? fieldById(schema, filter.fieldId) : hop?.field;
  const noun =
    filter.via === undefined
      ? (field?.label ?? t('insight.a_field'))
      : hopWord(schema, filter.via, filter.fieldId, targets);
  const verb = t(`insight.op.${filter.op}`);
  if (PRESENCE_OPS.includes(filter.op)) return `${noun} ${verb}`;
  if (filter.value === undefined) return `${noun} ${verb}`;

  if (NUMERIC_OPS.includes(filter.op)) {
    const amount =
      field?.type === 'money'
        ? formatMoney(Number(filter.value), schema.currency)
        : formatNumber(Number(filter.value));
    return `${noun} ${verb} ${amount}`;
  }
  const home = filter.via === undefined || hop === null ? schema : (targets.get(hop.via.targetRecordId ?? '') ?? schema);
  const wordOf = (value: string | number): string => nameForKey(home, filter.fieldId, String(value), names);
  if (Array.isArray(filter.value)) return `${noun} ${verb} ${spellValues(filter.value.map(wordOf))}`;
  return `${noun} ${verb} ${wordOf(filter.value as string | number)}`;
};

/** How many of a condition's several values a sentence names before it counts the rest. */
const VALUES_IN_A_SENTENCE = 2;

/**
 * Several values, in words: "Cash or UPI", and past two, "Kavya Clinic or
 * Venkat Rao +2 more". Joined by "or" as the list's own filter joins them,
 * because "is any of" is what the condition means.
 */
export const spellValues = (words: readonly string[]): string => {
  const spelled = words.slice(0, VALUES_IN_A_SENTENCE).join(` ${t('filter.or')} `);
  const rest = words.length - VALUES_IN_A_SENTENCE;
  return rest > 0 ? `${spelled} ${t('insight.more_values', { count: rest })}` : spelled;
};

/** Every condition, in words, with the tail counted rather than listed. */
const describeFilters = (
  schema: RecordSchemaResponse,
  filters: readonly InsightFilter[] | undefined,
  names: Readonly<Record<string, string>>,
  targets: HopSchemas = NO_TARGETS,
): string => {
  if (filters === undefined || filters.length === 0) return '';
  const spelled = filters
    .slice(0, CONDITIONS_IN_A_NAME)
    .map((filter) => describeFilter(schema, filter, names, targets));
  const rest = filters.length - spelled.length;
  const tail = rest > 0 ? [t('insight.more_conditions', { count: rest })] : [];
  return [...spelled, ...tail].join(` ${t('insight.and')} `);
};

/**
 * The card's own name.
 *
 * Three sources, in this order.
 *
 *   title     what the person typed. Free text they own, so it wins outright
 *             and nothing is appended to it — a name somebody chose is not
 *             ours to extend.
 *   labelKey  what the template called the tile. "Today" and "Net balance"
 *             survive exactly as the template wrote them.
 *   derived   a sentence built from the measure, the window, the group and the
 *             conditions, so a card is never unlabelled and two cards asking
 *             different questions never read the same.
 */
export const labelFor = (
  schema: RecordSchemaResponse,
  entry: Pick<InsightLayoutEntry, 'spec' | 'labelKey'> & { readonly title?: string | null },
  names: Readonly<Record<string, string>> = {},
  /**
   * The schemas its references reach, so a hop group and a hop condition are
   * named rather than spelled as ids. Every caller that has them hands them in;
   * one that has none still gets a true sentence, just a plainer one.
   */
  targets: HopSchemas = NO_TARGETS,
): string => {
  if (entry.title !== undefined && entry.title !== null && entry.title !== '') return entry.title;
  const { spec } = entry;
  const conditions = describeFilters(schema, spec.filters, names, targets);
  // A template's own word plus the conditions: the word stays first, so
  // "Today" is still recognisable, and the condition explains which today.
  //
  // `tOr`, not `t`: the key comes from a stored view or a stored layout, and a
  // key this build does not hold falls through to the derived sentence rather
  // than taking the strip down.
  if (entry.labelKey !== null) {
    const word = tOr(entry.labelKey, '');
    if (word !== '') return conditions === '' ? word : `${word}, ${conditions}`;
  }

  const base = derivedBase(schema, spec, periodWord(spec), names, targets);
  return conditions === '' ? base : `${base}, ${conditions}`;
};

/**
 * The words a card's own period wears: "this month", "last 7 days".
 *
 * Split out because a session filter replaces the period, and the sentence then
 * has to name the filter's days instead of the spec's. Everything else about
 * the name is unchanged, which is why only this one piece moved.
 */
const periodWord = (spec: InsightSpec): string =>
  spec.shape === 'series'
    ? t('insight.last_days', { days: spec.days })
    : scopeWord(spec.scope);

/**
 * The sentence a card makes of itself, given the words for its period.
 *
 * `labelFor` passes the spec's own period. `labelUnderFilter` passes the
 * filter's, because under a date filter the spec's period is no longer the
 * period the number covers.
 */
const derivedBase = (
  schema: RecordSchemaResponse,
  spec: InsightSpec,
  period: string,
  names: Readonly<Record<string, string>> = {},
  targets: HopSchemas = NO_TARGETS,
): string => {
  // A combined card names the records it reads, because no one field and no one
  // aggregate is true of it: "Deliveries minus Payments, all time". A breakdown
  // of one says how it is split, which is the whole card: "by customer".
  if (spec.measure.agg === 'formula') {
    const both = combinedWord(schema, spec.measure.expr, names);
    if (spec.shape !== 'breakdown') return `${both}, ${period}`;
    const group = groupWord(schema, spec.groupFieldId, spec.groupVia, targets);
    return `${both} ${t('insight.by')} ${group.toLowerCase()}, ${period}`.trim();
  }
  const noun = measureWord(schema, spec.measure);
  if (spec.shape === 'series') return `${noun}, ${period}`;
  if (spec.shape === 'breakdown') {
    const group = groupWord(schema, spec.groupFieldId, spec.groupVia, targets);
    return `${t('insight.top_of')} ${group.toLowerCase()}, ${period}`.trim();
  }
  if (spec.shape === 'scalar' && spec.leaderOf !== undefined) {
    const group = groupWord(schema, spec.leaderOf, spec.leaderVia, targets);
    return `${t('insight.top_of')} ${group.toLowerCase()}, ${period}`.trim();
  }
  // A recipe has no aggregate word worth wearing either way: the sentence
  // above already says what it works out, and a name the person typed replaces
  // all of it.
  const agg = t(`insight.agg.${spec.measure.agg}`);
  return `${agg} ${noun.toLowerCase()}, ${period}`;
};

/**
 * What an insight draws, in one line, ignoring any name it was given.
 *
 * A card that was named from a template shows "Today" as its name, and a
 * reader needs this beside it to know which question "Today" is asking — the
 * assistant's card list and the business presenter both use it (the Arrange
 * screen that first did went on 23 Sep 2026).
 */
export const describeInsight = (
  schema: RecordSchemaResponse,
  spec: InsightSpec,
  names: Readonly<Record<string, string>> = {},
  targets: HopSchemas = NO_TARGETS,
): string => labelFor(schema, { spec, labelKey: null }, names, targets);

/* -------------------------------------------------------------------- tone */

/**
 * Which way a number points.
 *
 * A signed measure is the only thing that has a direction: money out is
 * already negative in `signed_value`, so the sign is the answer and nothing
 * has to know what the record is about. A count has no direction at all.
 */
const isDirectional = (measure: InsightMeasure): boolean =>
  measure.agg !== 'rows' &&
  measure.agg !== 'count' &&
  measure.agg !== 'formula' &&
  measure.of.kind === 'measure';

const toneOf = (measure: InsightMeasure, value: number | null): InsightTone => {
  if (!isDirectional(measure) || value === null || value === 0) return 'neutral';
  return value > 0 ? 'positive' : 'negative';
};

/** The word for what a pair compares against. */
const againstWord = (scope: Exclude<InsightScope, 'all'>): string => t(`insight.vs_${scope}`);

/**
 * The change between two numbers, as a whole percent.
 *
 * A previous period of zero has no percentage — dividing by it would answer
 * infinity and the card would claim a rise it cannot measure. So the change is
 * null and the card simply shows the number.
 */
export const changeOf = (
  value: number | null,
  against: number | null,
): { percent: number; rising: boolean } | null => {
  if (value === null || against === null || against === 0) return null;
  const ratio = (value - against) / Math.abs(against);
  return { percent: Math.round(Math.abs(ratio) * 100), rising: value >= against };
};

/* ---------------------------------------------------------------- presenter */

const EMPTY = {
  change: null,
  progress: null,
  points: [] as readonly { day: string; value: number }[],
  latest: null,
  slices: [] as readonly PresentedSlice[],
  other: null,
} as const;

const presentScalar = (
  schema: RecordSchemaResponse,
  spec: Extract<InsightSpec, { shape: 'scalar' }>,
  result: ScalarResult,
  names: Readonly<Record<string, string>>,
  targets: HopSchemas,
  businessCurrency: string | null,
): Pick<Presented, 'value' | 'tone' | 'empty' | 'latest'> => {
  if (result.value === null) return { value: '', tone: 'neutral', empty: true, latest: null };
  const amount = asAmount(schema, spec.measure, result.value, businessCurrency);
  // A leader answers a name, with its total underneath. One number, one
  // label — which is why this needs no card of its own.
  if (spec.leaderOf !== undefined && result.key !== null) {
    return {
      value: keyWord(schema, spec.leaderOf, spec.leaderVia, result.key, names, targets),
      tone: 'neutral',
      empty: false,
      latest: amount,
    };
  }
  return {
    value: amount,
    tone: toneOf(spec.measure, result.value),
    empty: false,
    latest: null,
  };
};

const presentPair = (
  schema: RecordSchemaResponse,
  spec: Extract<InsightSpec, { shape: 'scalar_pair' }>,
  result: PairResult,
  businessCurrency: string | null,
): Pick<Presented, 'value' | 'tone' | 'empty' | 'change' | 'progress' | 'latest'> => {
  if (result.value === null && result.against === null) {
    return { value: '', tone: 'neutral', empty: true, change: null, progress: null, latest: null };
  }
  const value = result.value ?? 0;
  const change = changeOf(result.value, result.against);
  const signTone = toneOf(spec.measure, result.value);
  return {
    value: asAmount(schema, spec.measure, value, businessCurrency),
    tone: signTone,
    empty: false,
    change:
      change === null
        ? { percent: '', against: t('insight.no_compare'), tone: 'neutral' }
        : {
            percent: `${change.percent}%`,
            against: againstWord(spec.scope),
            // Good is always "the signed number moved up", and that one rule
            // covers both directions without anybody naming the business.
            // Earning more moves a money-in record up. Spending more moves a
            // money-out record down, because its signed_value is negative. So
            // `rising` is the whole answer, and a record's direction never has
            // to be looked up. A measure that is not signed — a count, a
            // quantity — has no good direction and says nothing.
            tone:
              !isDirectional(spec.measure) || change.percent === 0
                ? 'neutral'
                : change.rising
                  ? 'positive'
                  : 'negative',
          },
    // The TRUE ratio, never clamped. Beating last month by three quarters is
    // 177%, and rounding that down to 100% would be the card lying about the
    // one thing it exists to say. `MeterBar` clamps its own fill, so the bar
    // cannot overflow its track while the label still tells the truth.
    progress:
      result.against === null || result.against === 0
        ? null
        : Math.abs(value) / Math.abs(result.against),
    latest: result.against === null ? null : asAmount(schema, spec.measure, result.against, businessCurrency),
  };
};

const presentSeries = (
  schema: RecordSchemaResponse,
  spec: Extract<InsightSpec, { shape: 'series' }>,
  result: SeriesResult,
  businessCurrency: string | null,
): Pick<Presented, 'value' | 'tone' | 'empty' | 'points' | 'latest'> => {
  const total = result.points.reduce((sum, point) => sum + point.value, 0);
  const last = result.points.at(-1);
  return {
    value: asAmount(schema, spec.measure, total, businessCurrency),
    tone: 'neutral',
    // A flat line of zeros is not data. Every point zero means nothing landed
    // in the window, and the card should say so rather than draw a floor.
    empty: result.points.every((point) => point.value === 0),
    points: result.points,
    latest: last === undefined ? null : asAmount(schema, spec.measure, last.value, businessCurrency),
  };
};

const presentBreakdown = (
  schema: RecordSchemaResponse,
  spec: Extract<InsightSpec, { shape: 'breakdown' }>,
  result: BreakdownResult,
  names: Readonly<Record<string, string>>,
  targets: HopSchemas,
  businessCurrency: string | null,
): Pick<Presented, 'value' | 'tone' | 'empty' | 'slices' | 'other' | 'note'> => {
  if (result.slices.length === 0) {
    return { value: '', tone: 'neutral', empty: true, slices: [], other: null };
  }
  // Bars are drawn against the biggest slice, not against the total: the point
  // of a ranked list is how the rows compare to each other.
  const largest = Math.max(...result.slices.map((slice) => Math.abs(slice.value)));
  // Overlapping groups add up to more than the entries, so the headline is
  // the total the query counted once per entry, never the sum of the slices.
  const total =
    result.overlap !== undefined
      ? (result.overlap.total ?? 0)
      : result.slices.reduce((sum, slice) => sum + slice.value, 0) + (result.other?.value ?? 0);
  return {
    ...(result.overlap === undefined ? {} : { note: t('insight.groups_overlap') }),
    value: asAmount(schema, spec.measure, total, businessCurrency),
    tone: 'neutral',
    empty: false,
    slices: result.slices.map((slice) => ({
      label: keyWord(schema, spec.groupFieldId, spec.groupVia, slice.key, names, targets),
      value: asAmount(schema, spec.measure, slice.value, businessCurrency),
      fraction: largest === 0 ? 0 : Math.abs(slice.value) / largest,
    })),
    other:
      result.other === null
        ? null
        : {
            label: `${t('insight.other')}, ${formatNumber(result.other.count)}`,
            value: asAmount(schema, spec.measure, result.other.value, businessCurrency),
          },
  };
};

/**
 * One layout entry plus its result, as everything a card needs to draw.
 *
 * A null result — the record's view does not exist yet, because its schema has
 * not been pulled — presents as empty rather than as an error. The card draws a
 * quiet placeholder and fills in the moment the schema lands.
 *
 * @param names entry id to title, for group keys that point at a directory.
 *              `titlesForEntryIds` in entry-repo.ts answers it.
 */
export const presentInsight = (
  schema: RecordSchemaResponse,
  entry: InsightLayoutEntry,
  result: InsightResult | null,
  names: Readonly<Record<string, string>> = {},
  /**
   * The name to draw instead of the card's own. Set only when a session filter
   * changed what the number covers, and `labelUnderFilter` is what answers it.
   */
  label?: string,
  /**
   * The schemas its references reach. A card that groups through one is keyed
   * by a field on the other record, and only that record's schema can name the
   * keys or the group. Absent leaves both as they are stored, which is honest
   * and plain; `hopSchemasOf` in `hop-schemas.ts` is what the callers hand in.
   */
  targets: HopSchemas = NO_TARGETS,
  /**
   * The business's currency, for a worked-out amount on a record that carries
   * none of its own (the visits' Bill). `useBusinessCurrency` answers it.
   */
  businessCurrency: string | null = null,
): Presented => {
  const base = {
    id: entry.id,
    card: entry.card,
    span: entry.span,
    size: entry.size,
    label: label ?? labelFor(schema, entry, names, targets),
    worked: isWorkedOut(entry.spec.measure),
    // A record's own strip never samples: a record with no entries shows its
    // setup hint instead of a strip. Only the business strip does, and it says
    // so by asking `insight-sample.ts` for the flag after this presenter ran.
    sample: false,
  };

  if (result === null || result.shape !== entry.spec.shape) {
    return { ...base, ...EMPTY, value: '', tone: 'neutral', empty: true };
  }

  switch (entry.spec.shape) {
    case 'scalar':
      return {
        ...base,
        ...EMPTY,
        ...presentScalar(schema, entry.spec, result as ScalarResult, names, targets, businessCurrency),
      };
    case 'scalar_pair':
      return {
        ...base,
        ...EMPTY,
        ...presentPair(schema, entry.spec, result as PairResult, businessCurrency),
      };
    case 'series':
      return {
        ...base,
        ...EMPTY,
        ...presentSeries(schema, entry.spec, result as SeriesResult, businessCurrency),
      };
    case 'breakdown':
      return {
        ...base,
        ...EMPTY,
        ...presentBreakdown(schema, entry.spec, result as BreakdownResult, names, targets, businessCurrency),
      };
  }
};

/** Every group key a breakdown or a leader will need a name for. */
export const keysNeeding = (
  entries: readonly InsightLayoutEntry[],
  results: ReadonlyMap<string, InsightResult | null>,
): string[] => {
  const keys = new Set<string>();
  for (const entry of entries) {
    // A condition's value is an id too, and its name is needed for the card's
    // own label. It is known from the spec alone, with no result and no query,
    // so it is collected whether or not the number has arrived yet.
    for (const filter of entry.spec.filters ?? []) {
      if (typeof filter.value === 'string') keys.add(filter.value);
      if (Array.isArray(filter.value)) {
        for (const one of filter.value) if (typeof one === 'string') keys.add(one);
      }
    }
    const result = results.get(entry.id);
    if (result === null || result === undefined) continue;
    if (result.shape === 'breakdown') {
      for (const slice of result.slices) keys.add(slice.key);
    }
    if (result.shape === 'scalar' && result.key !== null) keys.add(result.key);
  }
  return [...keys];
};

/* --------------------------------------------------- the session filter */

/**
 * One of the list filter's conditions, in words.
 *
 * The verbs are the insight editor's own — `insight.op.is`, `at least`,
 * `is blank` — so a condition on a card and a filter on the list read the same
 * way. The field's label carries the noun, which is why nothing here knows what
 * a payment mode is.
 *
 * Several values on one field are joined by "or", because that is what the
 * condition means and there is no shorter honest way to say it.
 */
export const describeEntryFieldFilter = (
  schema: RecordSchemaResponse,
  filter: EntryFieldFilter,
  names: Readonly<Record<string, string>> = {},
): string => {
  const field = fieldById(schema, filter.fieldId);
  const noun = field?.label ?? t('insight.a_field');

  if (filter.op === 'set' || filter.op === 'unset') {
    return `${noun} ${t(`insight.op.${filter.op}`)}`;
  }
  if (filter.op === 'gte' || filter.op === 'lte') {
    const amount =
      field?.type === 'money'
        ? formatMoney(filter.value, schema.currency)
        : formatNumber(filter.value);
    return `${noun} ${t(`insight.op.${filter.op}`)} ${amount}`;
  }
  if (filter.op === 'any_of' || filter.op === 'none_of') {
    const words = filter.values
      .map((value) => nameForKey(schema, filter.fieldId, String(value), names))
      .join(` ${t('filter.or')} `);
    return `${noun} ${t(filter.op === 'any_of' ? 'insight.op.is' : 'insight.op.is_not')} ${words}`;
  }
  if (filter.op === 'all_of') {
    // "Services has Cut and Colour": every one of them, on one entry.
    const words = filter.values
      .map((value) => nameForKey(schema, filter.fieldId, String(value), names))
      .join(` ${t('filter.and')} `);
    return `${noun} ${t('insight.op.has')} ${words}`;
  }
  return noun;
};

/**
 * Every condition of the list filter, in words, with the tail counted.
 *
 * The same two-then-count trim `describeFilters` uses on a card's own
 * conditions, and grouped by field so a range reads as one clause rather than
 * two.
 */
export const describeEntryFilters = (
  schema: RecordSchemaResponse,
  fields: readonly EntryFieldFilter[],
  names: Readonly<Record<string, string>> = {},
): string => {
  if (fields.length === 0) return '';
  const spelled = fields
    .slice(0, CONDITIONS_IN_A_NAME)
    .map((filter) => describeEntryFieldFilter(schema, filter, names));
  const rest = fields.length - spelled.length;
  const tail = rest > 0 ? [t('insight.more_conditions', { count: rest })] : [];
  return [...spelled, ...tail].join(` ${t('insight.and')} `);
};

/** The days a filter covers, as a person would say them. */
export const windowWord = (window: EntryWindow): string =>
  window.kind === 'preset'
    ? t(`filter.preset.${window.preset}`)
    : t('filter.range_days', { from: formatDayShort(window.from), to: formatDayShort(window.to) });

/**
 * What a card is called while the list is filtered.
 *
 * Two rules, and the difference between them is the whole reason this function
 * exists rather than a line appended to `labelFor`.
 *
 * **Field conditions only.** The card still covers its own period, so its name
 * is still true. The filter's conditions are appended to it, exactly as a
 * card's own conditions already are: "Today" becomes "Today, Payment mode is
 * Cash". A name somebody typed survives, because the period it implied has not
 * moved.
 *
 * **A date window.** The period moved, so a name that states a period is no
 * longer true — and that includes a name a person typed and a word a template
 * chose. Both are replaced by a sentence built from the filter's own days.
 * Keeping "Today" over a last-month number would be the one failure worse than
 * a missing card.
 */
export const labelUnderFilter = (
  schema: RecordSchemaResponse,
  entry: InsightLayoutEntry,
  set: EntryFilterSet,
  names: Readonly<Record<string, string>> = {},
  targets: HopSchemas = NO_TARGETS,
): string => {
  const cut = describeEntryFilters(schema, set.fields, names);
  if (set.window === null) {
    const own = labelFor(schema, entry, names, targets);
    return cut === '' ? own : `${own}, ${cut}`;
  }
  const base = derivedBase(schema, entry.spec, windowWord(set.window), names, targets);
  const own = describeFilters(schema, entry.spec.filters, names, targets);
  const both = [own, cut].filter((words) => words !== '').join(` ${t('insight.and')} `);
  return both === '' ? base : `${base}, ${both}`;
};


/* ------------------------------------------------------ what a size draws */

/**
 * The width a card was given to draw at, or `undefined` for every caller that
 * predates the record's grid.
 *
 * `undefined` is not a missing value, it is a CALLER: the Dashboard's strip,
 * the insight gallery and the editor's preview all lay out by `span` and must
 * go on drawing exactly what they drew. Only `InsightGrid` passes a size, and
 * it passes the RECTANGLE it laid out rather than `Presented.size` — a lone
 * Small is stretched to the whole row in view mode and is told `wide` while
 * the insight is still stored `small`.
 */
export type DrawSize = InsightLayoutEntry['size'] | undefined;

/**
 * Which rung a card's headline number takes.
 *
 * `full` and `compact` are `METRIC`'s two, in `packages/ui`. `tight` is one
 * rung under `compact` and exists for a single rule: **a number never
 * truncates.** An ellipsis inside a number is a lie about money, so a Small
 * figure that will not fit drops a rung rather than losing a digit.
 */
export type MetricRung = 'full' | 'compact' | 'tight';

/**
 * How many characters a Small cell holds, per rung.
 *
 * **Arithmetic, not a measurement, and the one number in this step that wants
 * a device.** A Small cell is 158 wide on a 360pt phone and the tile spends 34
 * of it on padding and borders (`inner = w − 34`, the plan's geometry table),
 * leaving 124. The plan's own device line requires `₹1,80,737` — nine
 * characters — to fit at Small, which puts Sora semibold's advance at no more
 * than 13.8pt against Heading large's 20px; a tenth is at or past the edge.
 * Heading medium is 18px, and the same advance scaled to it gives eleven.
 *
 * The honest version is `onTextLayout` on the `Heading` that drew it, and
 * `HeadingProps` carries no such prop — a `packages/ui` change, outside the
 * step that added these drawings. Until then the budget errs SHORT: a rung too
 * small is quiet, a truncated number is wrong.
 */
export const SMALL_FIGURE_BUDGET = 9;
export const TIGHT_FIGURE_BUDGET = 11;

/** Code points, not UTF-16 units: Devanagari digits and `₹` each count once. */
const figureLength = (value: string): number => [...value].length;

/**
 * Whether a figure has run out of rungs at Small.
 *
 * §3.3's second fallback — *if it still does not fit, the card draws at Wide
 * width in view mode* — belongs to the grid and not to a card: only the grid
 * knows what else is on the row and whether widening one cell leaves a hole.
 * This is the question it asks.
 *
 * `InsightGrid` asks it before it packs and widens the cell (Phase 3). Phase
 * 6's resize should ask it too, and not offer Small for a figure it rejects.
 */
export const overflowsSmall = (value: string): boolean =>
  figureLength(value) > TIGHT_FIGURE_BUDGET;

/**
 * The rung, from the width the card was given.
 *
 * With no size this is the ternary the two metric cards have always spelled,
 * moved to one place: a full-width card is loud, a half-width one is compact.
 *
 * @param canDrop false when the value is a NAME rather than a figure — a
 *                leader's "Top item" is words, it already truncates to one
 *                line, and shrinking a name buys nothing.
 */
export const metricRungFor = (
  presented: Pick<Presented, 'span' | 'value'>,
  drawAs: DrawSize,
  canDrop = true,
): MetricRung => {
  if (drawAs === undefined) return presented.span === 'full' ? 'full' : 'compact';
  if (drawAs !== 'small') return 'full';
  return canDrop && figureLength(presented.value) > SMALL_FIGURE_BUDGET ? 'tight' : 'compact';
};

/**
 * How many periods a bars card shows, counting back from the latest.
 *
 * Seven and fourteen are §3.3's. The window the query asked for may be longer
 * than either; the LAST bars are the ones kept, because the bar that matters
 * on a card this size is the one you are standing in.
 */
export const BARS_AT = { wide: 7, large: 14 } as const;

/**
 * A tick under every bar crowds a 390pt screen once there are more than a
 * week of them, so past that only the ends and the middle are labelled.
 */
const TICK_EVERY = (count: number): number => (count <= 8 ? 1 : Math.ceil(count / 4));

/**
 * The bars one size draws, with the weekday under each that carries one.
 *
 * **A Wide names none of them**, and that is the whole difference between the
 * two sizes: a Wide cell is one row unit, which leaves 52pt of body and
 * nowhere to put a word. Seven unlabelled shapes still read as a week.
 *
 * With no size — the strip, the gallery, the preview — every point is drawn
 * and ticked exactly as before.
 */
export const barsFor = (
  points: Presented['points'],
  drawAs: DrawSize,
): readonly { readonly value: number; readonly tick: string }[] => {
  const large = drawAs === 'large';
  const kept =
    drawAs === undefined ? points : points.slice(-(large ? BARS_AT.large : BARS_AT.wide));
  const ticked = drawAs === undefined || large;
  const every = TICK_EVERY(kept.length);
  return kept.map((point, at) => ({
    value: point.value,
    // Count back from the end, so the last bar is always labelled.
    tick: ticked && (kept.length - 1 - at) % every === 0 ? formatWeekday(point.day) : '',
  }));
};

/** How far down the ranking each size reads. §3.3's two numbers. */
export const ROWS_AT = { wide: 2, large: 5 } as const;

/** A breakdown's tail row: a label, and a figure only where there is one. */
export type BreakdownTail = { readonly label: string; readonly value: string | null };

/**
 * The rows and the tail one size draws.
 *
 * A Wide holds **two** — a leader and its runner-up, which is the question a
 * one-unit cell can answer — and no tail, because a divider inside 52pt is a
 * rule with nothing on either side of it.
 *
 * A Large holds **five**, then a divider, then a tail row that is always
 * there. It is one of three things:
 *
 *   the query's own   "Other, 12" — the presenter netted the rows past the
 *                     card's limit and knows the count and the total.
 *   ours              "Other", with no figure, when the card's LIMIT let more
 *                     than five rows through. The rows exist and are ranked,
 *                     but their values are formatted strings by now and there
 *                     is no honest way to add them up, so the row says there
 *                     are more and stops.
 *   nothing left      "Not recorded". The ranking really did end.
 *
 * The divider therefore always means the same thing — *this is the end of it*.
 */
export const breakdownRows = (
  presented: Pick<Presented, 'slices' | 'other'>,
  drawAs: DrawSize,
): { readonly slices: Presented['slices']; readonly tail: BreakdownTail | null } => {
  const { slices, other } = presented;
  if (drawAs === undefined) return { slices, tail: other };
  if (drawAs !== 'large') return { slices: slices.slice(0, ROWS_AT.wide), tail: null };
  const kept = slices.slice(0, ROWS_AT.large);
  const tail =
    other ??
    (slices.length > kept.length
      ? { label: t('insight.other'), value: null }
      : { label: t('insight.not_recorded'), value: null });
  return { slices: kept, tail };
};
